import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";

import { ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { tournaments } from "../../lib/tournamentApi";
import { Pill } from "./shared";

const STATUS = { not_sent: ["muted", "Not sent"], sent: ["amber", "Sent"], claimed: ["green", "Claimed"], expired: ["red", "Expired"] };
const BLOCKED = { division_in_progress: "Division still in progress", no_captain_email: "No captain email" };

export default function ClaimsPanel({ t }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => tournaments.claims(t.id).then(setD).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [t.id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function send(ids) {
    setBusy(true);
    try {
      const r = await tournaments.sendClaims(t.id, ids);
      toast.success(r.sent.length ? `Sent ${r.sent.length} invite${r.sent.length > 1 ? "s" : ""}.` : "Nothing to send right now.");
      if (r.skipped.some((s) => s.reason === "sent_recently")) toast.message("Some invites were sent in the last 24h and were skipped.");
      await load();
    } catch (e) { toast.error(e.message || "Couldn't send."); }
    finally { setBusy(false); }
  }
  if (!d) return null;
  const eligible = d.claims.filter((c) => c.eligible);
  return (
    <div data-testid="claims-panel">
      <SectionTitle label="Claim your team" />
      <div data-testid="claims-explainer" style={{ fontSize: 12, color: "var(--chalk-muted)", marginBottom: 8, lineHeight: 1.5 }}>
        After a division finishes, email each captain a one-time link to set up their team on ChalkOS for next season.
        {d.roster_included ? " Adult tournament: the roster (first name, last initial, jersey) comes along."
                           : " Youth tournament: only the team name and sport carry over. No player data, ever."}
      </div>
      <ChalkActionButton label={busy ? "Sending…" : `Send invites to ${eligible.length} team${eligible.length === 1 ? "" : "s"}`}
                         testId="claims-send-all" disabled={busy || !eligible.length} onClick={() => send(null)} icon={<Send size={15} />} />
      <div style={{ marginTop: 10 }}>
        {d.claims.map((c) => {
          const [tone, label] = STATUS[c.status] || STATUS.not_sent;
          return (
            <ChalkCard key={c.team_id} data-testid={`claim-row-${c.team_id}`} style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 11, color: "var(--chalk-muted)" }}>{BLOCKED[c.blocked_reason] || c.captain_email}</div>
              </div>
              <Pill tone={tone} testId={`claim-status-${c.team_id}`}>{label}</Pill>
              {c.eligible && (
                <button data-testid={`claim-send-${c.team_id}`} disabled={busy} onClick={() => send([c.team_id])}
                        style={{ background: "transparent", border: "0.5px solid var(--chalk-border)", borderRadius: 8, padding: "5px 10px",
                                 color: "var(--green-mid)", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                  {c.status === "not_sent" ? "Send" : "Resend"}
                </button>
              )}
            </ChalkCard>
          );
        })}
      </div>
    </div>
  );
}
